import { convertClientJobToPromise } from "./client/clientJobToPromise.js";
import { createJobToPopulateNextJobs } from "./job/jobToPopulateNextJobs.js";
import { LOCAL_STORAGE_KEY_RESTORE_JOBS } from "./restoreFromLocalStorage.js";
import { convertServerJobToPromise } from "./server/serverJobToPromise.js";
import { JOB_DONE_EVENT_PREFIX } from "../types/events/jobDone.js";
import { JOBS_DELETED_EVENT } from "../types/events/jobsDeleted.js";
import { JOBS_RETRIED_EVENT } from "../types/events/jobsRetried.js";
import { localStorageTest } from "../utils/localStorageTest.js";
import { getStaticOptions } from "../utils/options.js";
/**
 * This class should be minimal so we can control the queue items itself.
 *
 * @see https://github.com/sindresorhus/p-queue#custom-RealQueueClass
 * @see https://github.com/sindresorhus/p-queue/blob/76b81cd707a6cd31b41f25e5d7fa8abc7486c4bf/source/priority-queue.ts#L12
 */ class RealQueueClass {
    constructor(){
        this.queue = [];
        this.alreadyAddedJobIds = [];
        this.queue = [];
        // This is a bit hacky, but currently the only way to access the parent instance (?!)
        window.realQueueInstance = this;
        this.listenRetries();
        this.listenDeletions();
    }
    enqueueFromList(list) {
        const tryJobIds = list.filter(({ worker })=>worker === "server").map(({ id })=>id);
        const jobs = [];
        const { handler, alreadyAddedJobIds } = this;
        for (const job of list){
            const { id, type } = job;
            // Avoid duplicate items
            if (alreadyAddedJobIds.indexOf(id) > -1) {
                continue;
            }
            if (job.worker === "client") {
                jobs.push({
                    runs: convertClientJobToPromise(this, job),
                    job
                });
            } else {
                // The next request is not allowed to retry the previous one
                tryJobIds.splice(tryJobIds.indexOf(id), 1);
                jobs.push({
                    runs: convertServerJobToPromise(this, job, [
                        /* pass immutable */ ...tryJobIds
                    ]),
                    job
                });
            }
            alreadyAddedJobIds.push(id);
            // Listen to failed job and make the job available again to retry
            const listenerFailedEvent = `${JOB_DONE_EVENT_PREFIX}${type}`;
            const listenerFailed = ({ detail: { job: { id: doneJobId }, success } })=>{
                if (doneJobId === id && !success) {
                    document.removeEventListener(listenerFailedEvent, listenerFailed);
                    const alreadyIdx = this.alreadyAddedJobIds.indexOf(doneJobId);
                    if (alreadyIdx > -1) {
                        this.alreadyAddedJobIds.splice(alreadyIdx, 1);
                    }
                }
            };
            document.addEventListener(listenerFailedEvent, listenerFailed);
        }
        // Add a refresh job to populate our queue with the next "list"
        if (jobs.length > 0) {
            const previousAt = jobs.length > 40 ? 10 : 0;
            const { job: previousJob } = jobs[previousAt];
            jobs.splice(jobs.length - previousAt, 0, {
                runs: [
                    createJobToPopulateNextJobs(list[list.length - 1].id, this)
                ],
                job: previousJob
            });
        }
        // Make this list restorable from the localStorage
        if (localStorageTest()) {
            const lsKey = `${LOCAL_STORAGE_KEY_RESTORE_JOBS}-${getStaticOptions().localStorageSuffix}`;
            const restorable = localStorage.getItem(lsKey)?.split(",").map(Number) || [];
            list.forEach(({ id })=>restorable.indexOf(id) === -1 && restorable.push(id));
            localStorage.setItem(lsKey, restorable.join(","));
        }
        jobs.forEach(({ job, runs })=>{
            handler.addAll(runs, {
                job
            });
        });
    }
    enqueue(run, options) {
        if (!options?.job) {
            throw new Error(`Please provide a job object!`);
        }
        const { job: { id, type } } = options;
        const element = {
            ...options,
            id: id.toString(),
            type,
            run
        };
        this.queue.push(element);
    }
    dequeue() {
        const item = this.queue.shift();
        return item?.run;
    }
    get size() {
        return this.queue.length;
    }
    filterQueueItem(options) {
        return this.queue.filter(({ id, type })=>{
            if (typeof options.id !== "undefined" && id !== options.id) {
                return false;
            }
            if (typeof options.type !== "undefined" && type !== options.type) {
                return false;
            }
            return true;
        });
    }
    filter(options) {
        return this.filterQueueItem(options).map((element)=>element.run);
    }
    get handler() {
        return window.realQueueInstancePQueue;
    }
    removeByType(type, forgetAboutAlreadyAdded = true) {
        const removedIds = [];
        this.queue = this.queue.filter((item)=>{
            const result = item.type !== type;
            if (!result) {
                removedIds.push(+item.id);
            }
            return result;
        });
        if (forgetAboutAlreadyAdded) {
            this.alreadyAddedJobIds = this.alreadyAddedJobIds.filter((id)=>removedIds.indexOf(id) === -1);
        }
    }
    /**
     * Listen if jobs got deleted, remove them from the queue so they never get executed again.
     */ listenDeletions() {
        const listener = async ({ detail: { params: { type } } })=>{
            this.removeByType(type, false);
        };
        document.addEventListener(JOBS_DELETED_EVENT, listener);
    }
    /**
     * Listen if jobs got retried, remove them from the queue so they can be added again.
     */ listenRetries() {
        const listener = async ({ detail: { request: { type } } })=>{
            this.removeByType(type);
        };
        document.addEventListener(JOBS_RETRIED_EVENT, listener);
    }
    setPriority(id, priority) {
    // Currently not implemented in real-queue
    }
}
export { RealQueueClass };
